import React from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { useNavigate } from 'react-router-dom';
import StaticPageLayout from '../components/layouts/StaticPageLayout';

const LandingPage = () => {
    const navigate = useNavigate();

    const navigateToInterestng = () => {
        window.location.href = 'https://app.interestng.com/app';
    };

    const features = [
        {
            title: 'Loan Comparison Tool',
            description: 'Compare personal loan, home loan, business loan and other loan offers',
            icon: 'pi pi-id-card',
            color: 'text-green-500'
        },
        {
            title: 'Loan Eligibility Check',
            description: 'Instant loan eligibility assessment based on comprehensive credit analysis',
            icon: 'pi pi-check-circle',
            color: 'text-orange-500'
        },
        {
            title: 'Credit Bureau Reports',
            description: 'Access comprehensive credit reports from multiple bureaus instantly',
            icon: 'pi pi-file-check',
            color: 'text-blue-500'
        },
        {
            title: 'Bank Statement Analysis',
            description: 'Advanced AI-powered analysis of bank statements for credit assessment',
            icon: 'pi pi-chart-line',
            color: 'text-purple-500'
        },
        {
            title: 'Real-time Processing',
            description: 'Get instant results with our high-performance processing engine',
            icon: 'pi pi-bolt',
            color: 'text-yellow-500'
        },
        {
            title: 'Secure & Compliant',
            description: 'Secured with SSL certificate',
            icon: 'pi pi-shield',
            color: 'text-red-500'
        }
    ];

    const stats = [
        { number: '50K+', label: 'Active Users' },
        { number: '1M+', label: 'Reports Generated' },
        { number: '99.9%', label: 'Uptime' },
        { number: '24/7', label: 'Support' }
    ];

    return (
        <StaticPageLayout>
            {/* Hero Section */}
            <div className="relative overflow-hidden" style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                minHeight: '600px' 
            }}>
                <div className="absolute w-full h-full opacity-10">
                    <div className="absolute" style={{ 
                        top: '-10%', 
                        right: '-5%',
                        width: '400px',
                        height: '400px',
                        borderRadius: '50%',
                        background: 'white'
                    }}></div>
                    <div className="absolute" style={{ 
                        bottom: '-15%', 
                        left: '-10%',
                        width: '500px',
                        height: '500px',
                        borderRadius: '50%',
                        background: 'white'
                    }}></div>
                </div>
                
                <div className="relative z-1 px-4 py-8 md:py-12 lg:px-8">
                    <div className="grid align-items-center">
                        <div className="col-12 md:col-6 text-center md:text-left text-white">
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 line-height-2">
                                Your Trusted Partner for
                                <span className="block text-yellow-300">Financial Verification</span>
                            </h1>
                            <p className="text-lg md:text-xl mb-6 line-height-3 opacity-90">
                                Access loan comparision tools, comprehensive credit reports, employment verification, 
                                and advanced bank statement analysis - all in one powerful platform.
                            </p>
                            
                            <div className="flex gap-3 justify-content-center md:justify-content-start flex-wrap">
                                <Button 
                                    label="Start Free Trial" 
                                    icon="pi pi-arrow-right"
                                    className="p-button-rounded p-button-lg bg-white text-primary-500 border-none font-bold px-5"
                                    onClick={navigateToInterestng}
                                />
                            </div>
                        </div>
                        <div className="col-12 md:col-6 mt-6 md:mt-0">
                            <div className="text-center">
                                <img 
                                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600" 
                                    alt="Financial Dashboard" 
                                    className="w-full max-w-30rem border-round-xl shadow-8"
                                    style={{ maxHeight: '400px', objectFit: 'cover' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            <div className="py-6 md:py-8" style={{ backgroundColor: '#1e1e2e' }}>
                <div className="px-4 lg:px-8">
                    <div className="grid text-center">
                        {stats.map((stat, index) => (
                            <div key={index} className="col-12 sm:col-6 md:col-3">
                                <div className="p-4">
                                    <h2 className="text-4xl md:text-5xl font-bold text-primary mb-2">{stat.number}</h2>
                                    <p className="text-gray-300 text-lg">{stat.label}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div className="py-8 px-4 lg:px-8" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="text-center mb-8">
                    <span className="text-primary font-semibold text-lg">OUR FEATURES</span>
                    <h2 className="text-4xl md:text-5xl font-bold text-900 mt-3 mb-4">
                        Everything You Need for Financial Verification
                    </h2>
                    <p className="text-xl text-600 max-w-4xl mx-auto">
                        Comprehensive suite of tools designed to streamline your financial verification processes
                    </p>
                </div>
                
                <div className="grid">
                    {features.map((feature, index) => (
                        <div key={index} className="col-12 md:col-6 lg:col-4">
                            <Card className="h-full shadow-2 border-round-xl hover:shadow-8 transition-all transition-duration-300">
                                <div className="flex flex-column align-items-center text-center">
                                    <div className="bg-primary-50 p-3 border-round-xl mb-4" 
                                         style={{ width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <i className={`${feature.icon} ${feature.color} text-3xl`}></i>
                                    </div>
                                    <h3 className="text-xl font-bold text-900 mb-3">{feature.title}</h3>
                                    <p className="text-gray-600 line-height-3 m-0">{feature.description}</p>
                                </div>
                            </Card>
                        </div>
                    ))}
                </div>
            </div>

            {/* How It Works Section */}
            <div className="py-8 px-4 lg:px-8">
                <div className="text-center mb-8">
                    <span className="text-primary font-semibold text-lg">HOW IT WORKS</span>
                    <h2 className="text-4xl md:text-5xl font-bold text-900 mt-3 mb-4">
                        Get Started in 3 Simple Steps
                    </h2>
                </div>
                
                <div className="grid">
                    <div className="col-12 md:col-4 text-center">
                        <div className="mb-4">
                            <div className="bg-primary inline-flex align-items-center justify-content-center border-circle text-white font-bold mb-4" 
                                 style={{ width: '80px', height: '80px', fontSize: '2rem' }}>1</div>
                            <h3 className="text-2xl font-bold text-900 mb-3">Sign Up</h3>
                            <p className="text-lg text-700 line-height-3">
                                Create your account and get instant access to our verification platform
                            </p>
                        </div>
                    </div>
                    
                    <div className="col-12 md:col-4 text-center">
                        <div className="mb-4">
                            <div className="bg-primary inline-flex align-items-center justify-content-center border-circle text-white font-bold mb-4" 
                                 style={{ width: '80px', height: '80px', fontSize: '2rem' }}>2</div>
                            <h3 className="text-2xl font-bold text-900 mb-3">Check Eligibility</h3>
                            <p className="text-lg text-700 line-height-3">
                                Check your loan eligibility instantly and get detailed credit reports
                            </p>
                        </div>
                    </div>
                    
                    <div className="col-12 md:col-4 text-center">
                        <div className="mb-4">
                            <div className="bg-primary inline-flex align-items-center justify-content-center border-circle text-white font-bold mb-4" 
                                 style={{ width: '80px', height: '80px', fontSize: '2rem' }}>3</div>
                            <h3 className="text-2xl font-bold text-900 mb-3">Verify</h3>
                            <p className="text-lg text-700 line-height-3">
                                Start verifying documents and generating reports instantly
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Testimonial Section */}
            <div className="py-8 px-4 lg:px-8" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="text-center mb-8">
                    <span className="text-primary font-semibold text-lg">TESTIMONIALS</span>
                    <h2 className="text-4xl md:text-5xl font-bold text-900 mt-3 mb-4">
                        What Our Clients Say
                    </h2>
                </div>
                
                <div className="grid">
                    <div className="col-12 md:col-6 lg:col-4">
                        <Card className="h-full shadow-2 border-round-xl">
                            <div className="text-center">
                                <div className="mb-4">
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl"></i>
                                </div>
                                <p className="text-lg text-700 line-height-3 mb-4">
                                    "Interestng has transformed our verification process. What used to take hours now takes minutes!"
                                </p>
                                <div className="flex align-items-center justify-content-center">
                                    <div className="bg-primary-100 border-circle mr-3" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <i className="pi pi-user text-primary text-xl"></i>
                                    </div>
                                    <div className="text-left">
                                        <h4 className="font-bold m-0">Rajesh Kumar</h4>
                                        <p className="text-600 m-0">Finance Manager</p>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                    
                    <div className="col-12 md:col-6 lg:col-4">
                        <Card className="h-full shadow-2 border-round-xl">
                            <div className="text-center">
                                <div className="mb-4">
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl"></i>
                                </div>
                                <p className="text-lg text-700 line-height-3 mb-4">
                                    "The loan eligibility check was instant and the credit reports are very detailed. Highly recommended!"
                                </p>
                                <div className="flex align-items-center justify-content-center">
                                    <div className="bg-primary-100 border-circle mr-3" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <i className="pi pi-user text-primary text-xl"></i>
                                    </div>
                                    <div className="text-left">
                                        <h4 className="font-bold m-0">Priya Sharma</h4>
                                        <p className="text-600 m-0">Loan agent</p>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                    
                    <div className="col-12 lg:col-4">
                        <Card className="h-full shadow-2 border-round-xl">
                            <div className="text-center">
                                <div className="mb-4">
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl mr-1"></i>
                                    <i className="pi pi-star-fill text-yellow-500 text-xl"></i>
                                </div>
                                <p className="text-lg text-700 line-height-3 mb-4">
                                    "Reliable, fast, and accurate. Interestng has become an essential part of our workflow."
                                </p>
                                <div className="flex align-items-center justify-content-center">
                                    <div className="bg-primary-100 border-circle mr-3" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <i className="pi pi-user text-primary text-xl"></i>
                                    </div>
                                    <div className="text-left">
                                        <h4 className="font-bold m-0">Amit Patel</h4>
                                        <p className="text-600 m-0">Loan Officer</p>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="py-8 px-4 lg:px-8 text-center" style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
            }}>
                <div className="text-white">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                        Ready to Transform Your Verification Process?
                    </h2>
                    <p className="text-xl mb-6 opacity-90 max-w-4xl mx-auto">
                        Join thousands of businesses that trust Interestng for their financial verification needs. 
                        Start your free trial today and see the difference.
                    </p>
                    <div className="flex gap-4 justify-content-center flex-wrap">
                        <Button 
                            label="Start Free Trial" 
                            icon="pi pi-arrow-right" 
                            className="p-button-rounded p-button-lg bg-white text-primary-500 border-none font-bold px-6"
                            onClick={navigateToInterestng}
                        />
                    </div>
                </div>
            </div>
        </StaticPageLayout>
    );
};

export default LandingPage;